import React, { useState } from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { FileText, Download, Calendar, TrendingUp, Users, Globe, Award, DollarSign, Target, Lightbulb, CheckCircle } from 'lucide-react';

const AnnualReportsPage = () => {
  const arcaGreen = '#6B8E23';
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: FileText },
    { id: 'highlights', title: 'Highlights', icon: Award },
    { id: 'programs', title: 'Programs & Events', icon: TrendingUp },
    { id: 'membership', title: 'Membership Growth', icon: Users },
    { id: 'partnerships', title: 'Partnerships', icon: Globe },
    { id: 'financial', title: 'Financial Report', icon: DollarSign },
    { id: 'challenges', title: 'Challenges & Plans', icon: Target }
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <div 
        className="py-5 text-white"
        style={{ 
          background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)`,
          minHeight: '300px'
        }}
      >
        <div className="container py-5">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="display-4 fw-bold mb-4">Annual Activity Report 2025</h1>
              <p className="lead mb-3">
                Reporting Year: January – December 2025
              </p>
              <p className="mb-4">
                <small>Prepared by: Secretariat of the Africa Respiratory Care Association (ARCA)</small><br />
                <small>Date: December 2025</small>
              </p>
              <button className="btn btn-light btn-lg rounded-pill px-4" style={{ color: arcaGreen, fontWeight: '600' }}>
                <Download size={20} className="me-2" />
                Download Full Report (PDF)
              </button>
            </div>
            <div className="col-lg-4 text-center">
              <div 
                className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{ width: '120px', height: '120px' }}
              >
                <FileText style={{ color: arcaGreen }} size={60} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-light border-bottom sticky-top" style={{ top: '112px', zIndex: 100 }}>
        <div className="container">
          <div className="d-flex overflow-auto py-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  className={`btn btn-sm me-2 text-nowrap ${activeSection === section.id ? 'btn-success' : 'btn-outline-secondary'}`}
                  style={activeSection === section.id ? { backgroundColor: arcaGreen, borderColor: arcaGreen } : {}}
                  onClick={() => setActiveSection(section.id)}
                >
                  <Icon size={16} className="me-1" />
                  {section.title}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container py-5">
        
        {/* Introduction & Vision/Mission */}
        {activeSection === 'overview' && (
          <div>
            <div className="card border-0 shadow-sm mb-4">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <FileText size={24} className="me-2" />
                  Introduction
                </h2>
              </div>
              <div className="card-body p-4">
                <p className="lead text-dark">
                  The Africa Respiratory Care Association (ARCA) was established in January 2024 as a non-profit organization dedicated to advancing respiratory care education, advocacy, and professional development across Africa.
                </p>
                <p className="text-dark">
                  In 2025, ARCA continued to make significant strides in fulfilling its mission — expanding its membership base, hosting impactful programs, and collaborating with international partners to strengthen respiratory care awareness and education across the continent.
                </p>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div 
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px' }}
                    >
                      <Target style={{ color: arcaGreen }} size={28} />
                    </div>
                    <h3 className="h5 mb-3" style={{ color: arcaGreen }}>Our Vision</h3>
                    <p className="text-dark mb-0">
                      To be the leading voice for respiratory care in Africa, advancing respiratory health through education, research, advocacy, and clinical excellence.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <div 
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px' }}
                    >
                      <Lightbulb style={{ color: arcaGreen }} size={28} />
                    </div>
                    <h3 className="h5 mb-3" style={{ color: arcaGreen }}>Our Mission</h3>
                    <p className="text-dark mb-0">
                      To promote respiratory therapy as a profession, improve access to quality respiratory care services, and build capacity through professional development, collaboration, and public awareness initiatives.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mt-4">
              <div className="card-body p-4">
                <h3 className="h5 mb-3" style={{ color: arcaGreen }}>Strategic Goals for 2025</h3>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <CheckCircle style={{ color: arcaGreen }} size={20} className="me-2" />
                    Strengthen ARCA's visibility and presence across African countries
                  </li>
                  <li className="mb-2">
                    <CheckCircle style={{ color: arcaGreen }} size={20} className="me-2" />
                    Promote respiratory care education and professional training opportunities
                  </li>
                  <li className="mb-2">
                    <CheckCircle style={{ color: arcaGreen }} size={20} className="me-2" />
                    Build and sustain partnerships with global respiratory and health organizations
                  </li>
                  <li className="mb-2">
                    <CheckCircle style={{ color: arcaGreen }} size={20} className="me-2" />
                    Advocate for the inclusion of respiratory therapy in national health systems
                  </li>
                  <li className="mb-0">
                    <CheckCircle style={{ color: arcaGreen }} size={20} className="me-2" />
                    Foster professional development and continuing education for members
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Highlights */}
        {activeSection === 'highlights' && (
          <div>
            <div className="card border-0 shadow-sm">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <Award size={24} className="me-2" />
                  2025 Highlights
                </h2>
              </div>
              <div className="card-body p-4">
                <div className="row g-4 mb-4">
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <Users style={{ color: arcaGreen }} size={40} className="mb-2" />
                      <h3 className="display-6 fw-bold mb-1" style={{ color: arcaGreen }}>1,413</h3>
                      <p className="text-muted mb-0">Registered Members</p>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <Globe style={{ color: arcaGreen }} size={40} className="mb-2" />
                      <h3 className="display-6 fw-bold mb-1" style={{ color: arcaGreen }}>23</h3>
                      <p className="text-muted mb-0">African Countries</p>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <TrendingUp style={{ color: arcaGreen }} size={40} className="mb-2" />
                      <h3 className="display-6 fw-bold mb-1" style={{ color: arcaGreen }}>17</h3>
                      <p className="text-muted mb-0">Professional Webinars</p>
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6">
                    <div className="text-center">
                      <Award style={{ color: arcaGreen }} size={40} className="mb-2" />
                      <h3 className="display-6 fw-bold mb-1" style={{ color: arcaGreen }}>4+</h3>
                      <p className="text-muted mb-0">Major Events</p>
                    </div>
                  </div>
                </div>

                <h4 className="h5 mb-3" style={{ color: arcaGreen }}>Key Achievements</h4>
                <div className="list-group">
                  <div className="list-group-item border-0 bg-light mb-2 rounded">
                    <h5 className="mb-2" style={{ color: arcaGreen }}>Membership Growth</h5>
                    <p className="mb-0">ARCA maintained its Free Membership Base throughout 2025, leading to a remarkable increase in enrollment. Total registered members as of December 2025: 1,413, spanning 23 African countries.</p>
                  </div>
                  <div className="list-group-item border-0 bg-light mb-2 rounded">
                    <h5 className="mb-2" style={{ color: arcaGreen }}>Educational Programs</h5>
                    <p className="mb-0">Organized 17 professional webinar sessions (10 from March–September, and 7 in October). Topics included Respiratory Therapy Education in Africa, Asthma and COPD Management, Ventilator Management, and Oxygen Therapy.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Programs & Events */}
        {activeSection === 'programs' && (
          <div>
            <div className="card border-0 shadow-sm">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <TrendingUp size={24} className="me-2" />
                  Major Events and Projects
                </h2>
              </div>
              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>First Respiratory Care Event in Somalia</h5>
                        <p className="text-dark mb-2">
                          Hosted by the University of Bosaso, Garowe Campus, in collaboration with Global Health Alliance (GHA).
                        </p>
                        <p className="text-muted mb-0">
                          <strong>Topic:</strong> "What is Respiratory Care & Why It Matters for Saving Newborn Lives in Somalia"
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>Asthma Education in Ghana</h5>
                        <p className="text-dark mb-2">
                          Conducted at The Evergreen Royal Academy, Tema, Ghana in September 2025.
                        </p>
                        <p className="text-muted mb-0">
                          <strong>Participants:</strong> Faculty, staff, students, and parents
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>Advocacy in Nigeria</h5>
                        <p className="text-dark mb-2">
                          Led by Dr. Olugbenga Agbelemose, ARCA's Director of Projects/Programs and Country Director for Nigeria.
                        </p>
                        <p className="text-muted mb-0">
                          <strong>Impact:</strong> Advanced respiratory therapy profession recognition
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>Formation of LARC</h5>
                        <p className="text-dark mb-2">
                          Lebanese Association for Respiratory Care formed in collaboration with ARCA's Country Director for Lebanon.
                        </p>
                        <p className="text-muted mb-0">
                          <strong>Outcome:</strong> Strengthened respiratory care infrastructure in Lebanon
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 bg-light mt-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3" style={{ color: arcaGreen }}>Respiratory Care Week 2025</h5>
                    <p className="text-dark mb-2">
                      <strong>Dates:</strong> October 20–26, 2025
                    </p>
                    <p className="text-dark mb-3">
                      <strong>Theme:</strong> "Strengthening Respiratory Health Through Innovation and Collaboration"
                    </p>
                    <p className="text-dark mb-0">
                      <strong>Activities:</strong> Seven webinars, CPD program, and multiple awareness sessions across Africa
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Membership */}
        {activeSection === 'membership' && (
          <div>
            <div className="card border-0 shadow-sm">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <Users size={24} className="me-2" />
                  Membership Growth & Development
                </h2>
              </div>
              <div className="card-body p-4">
                <div className="row align-items-center mb-4">
                  <div className="col-md-8">
                    <h4 className="mb-3" style={{ color: arcaGreen }}>Free Membership Initiative</h4>
                    <p className="text-dark mb-3">
                      ARCA maintained its Free Membership Base throughout 2025, removing financial barriers and enabling widespread participation across the continent.
                    </p>
                    <p className="text-dark mb-0">
                      This strategic decision resulted in remarkable growth, demonstrating ARCA's commitment to inclusivity and professional development accessibility.
                    </p>
                  </div>
                  <div className="col-md-4 text-center">
                    <div 
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                      style={{ width: '100px', height: '100px' }}
                    >
                      <Users style={{ color: arcaGreen }} size={50} />
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card border-0 bg-light text-center">
                      <div className="card-body p-4">
                        <h3 className="display-4 fw-bold mb-2" style={{ color: arcaGreen }}>1,413</h3>
                        <p className="text-muted mb-0">Total Registered Members</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light text-center">
                      <div className="card-body p-4">
                        <h3 className="display-4 fw-bold mb-2" style={{ color: arcaGreen }}>23</h3>
                        <p className="text-muted mb-0">African Countries Represented</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-light rounded">
                  <h5 className="mb-3" style={{ color: arcaGreen }}>Organizational Development</h5>
                  <ul className="mb-0">
                    <li className="mb-2">Enhanced coordination across 23 represented countries through improved communication</li>
                    <li className="mb-2">Updated website (www.africaresp.org) with events and membership information</li>
                    <li className="mb-0">Secretariat supported program planning, partnership coordination, and communications</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Partnerships */}
        {activeSection === 'partnerships' && (
          <div>
            <div className="card border-0 shadow-sm">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <Globe size={24} className="me-2" />
                  Partnerships and Collaborations
                </h2>
              </div>
              <div className="card-body p-4">
                <p className="lead text-dark mb-4">
                  ARCA strengthened partnerships with leading global respiratory and health organizations throughout 2025.
                </p>

                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>Global Health Alliance (GHA)</h5>
                        <p className="text-dark mb-0">
                          Collaborated on the first respiratory care event in Somalia, bringing professional education to new territories.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>The Breathe Well Initiative (BWI)</h5>
                        <p className="text-dark mb-0">
                          Partnership focused on respiratory health awareness and patient education across multiple African countries.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>Global Asthma and COPD Outreach (GACO)</h5>
                        <p className="text-dark mb-0">
                          Joint initiatives in asthma and COPD management education, training, and public awareness.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-0 bg-light h-100">
                      <div className="card-body p-4">
                        <h5 className="mb-3" style={{ color: arcaGreen }}>International Council for Respiratory Care (ICRC)</h5>
                        <p className="text-dark mb-0">
                          Global partnership advancing respiratory care standards and professional development.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 bg-light mt-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3" style={{ color: arcaGreen }}>Corporate Sponsorship</h5>
                    <p className="text-dark mb-2">
                      <strong>Zopec Medical</strong> sponsored ARCA's Premium Zoom Package, enabling high-quality virtual programming.
                    </p>
                    <p className="text-muted mb-0">
                      Discussions with AARC (American Association for Respiratory Care) are ongoing for future collaboration opportunities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Report */}
        {activeSection === 'financial' && (
          <div>
            <div className="card border-0 shadow-sm">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <DollarSign size={24} className="me-2" />
                  Financial Overview
                </h2>
              </div>
              <div className="card-body p-4">
                <p className="lead text-dark mb-4">
                  ARCA's operations in 2025 were primarily supported by volunteer contributions and partnership sponsorships. No donations or paid memberships were recorded.
                </p>

                <div className="row g-4 mb-4">
                  <div className="col-md-4">
                    <div className="card border-0 bg-success bg-opacity-10 text-center">
                      <div className="card-body p-4">
                        <DollarSign style={{ color: arcaGreen }} size={40} className="mb-2" />
                        <h3 className="display-6 fw-bold mb-2" style={{ color: arcaGreen }}>$1,200</h3>
                        <p className="text-muted mb-0">Total Income (Sponsorship)</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card border-0 bg-danger bg-opacity-10 text-center">
                      <div className="card-body p-4">
                        <TrendingUp style={{ color: '#dc3545' }} size={40} className="mb-2" />
                        <h3 className="display-6 fw-bold mb-2 text-danger">$1,000</h3>
                        <p className="text-muted mb-0">Total Expenditure</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card border-0 bg-primary bg-opacity-10 text-center">
                      <div className="card-body p-4">
                        <CheckCircle style={{ color: '#0d6efd' }} size={40} className="mb-2" />
                        <h3 className="display-6 fw-bold mb-2 text-primary">$200</h3>
                        <p className="text-muted mb-0">Closing Balance</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 bg-light">
                  <div className="card-body p-4">
                    <h5 className="mb-3" style={{ color: arcaGreen }}>Income Sources</h5>
                    <ul className="mb-4">
                      <li>Zopec Medical Sponsorship: USD 1,200</li>
                    </ul>

                    <h5 className="mb-3" style={{ color: arcaGreen }}>Expenditure Breakdown</h5>
                    <ul className="mb-0">
                      <li className="mb-2">Program Development and Webinars</li>
                      <li className="mb-2">Operational Costs (Zoom subscriptions, communications)</li>
                      <li className="mb-2">Event Support and Materials</li>
                      <li className="mb-0">Website Maintenance</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Challenges & Future Plans */}
        {activeSection === 'challenges' && (
          <div>
            <div className="card border-0 shadow-sm mb-4">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <Target size={24} className="me-2" />
                  Challenges
                </h2>
              </div>
              <div className="card-body p-4">
                <ul className="list-unstyled">
                  <li className="mb-3 d-flex">
                    <span className="me-3 text-danger">•</span>
                    <span>Financial constraints and lack of consistent inflow</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <span className="me-3 text-danger">•</span>
                    <span>Limited funding for national-level initiatives</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <span className="me-3 text-danger">•</span>
                    <span>Challenges in reaching key stakeholders</span>
                  </li>
                  <li className="mb-0 d-flex">
                    <span className="me-3 text-danger">•</span>
                    <span>Need for stronger government-level recognition of the respiratory therapy profession</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <Lightbulb size={24} className="me-2" />
                  Plans for 2026 and Beyond
                </h2>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex align-items-start p-3 bg-light rounded">
                      <CheckCircle style={{ color: arcaGreen }} size={24} className="me-3 mt-1 flex-shrink-0" />
                      <div>
                        <h5 className="mb-2" style={{ color: arcaGreen }}>1st ARCA Annual Scientific Conference</h5>
                        <p className="mb-0 text-dark">Host a landmark event bringing together respiratory care professionals from across Africa and beyond.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-start p-3 bg-light rounded">
                      <CheckCircle style={{ color: arcaGreen }} size={24} className="me-3 mt-1 flex-shrink-0" />
                      <div>
                        <h5 className="mb-2" style={{ color: arcaGreen }}>Formal Training Programs</h5>
                        <p className="mb-0 text-dark">Develop respiratory therapy training programs in partnership with African universities.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-start p-3 bg-light rounded">
                      <CheckCircle style={{ color: arcaGreen }} size={24} className="me-3 mt-1 flex-shrink-0" />
                      <div>
                        <h5 className="mb-2" style={{ color: arcaGreen }}>Government & Institutional Partnerships</h5>
                        <p className="mb-0 text-dark">Strengthen partnerships with ministries of health and educational institutions across the continent.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-start p-3 bg-light rounded">
                      <CheckCircle style={{ color: arcaGreen }} size={24} className="me-3 mt-1 flex-shrink-0" />
                      <div>
                        <h5 className="mb-2" style={{ color: arcaGreen }}>Youth Ambassadors Program</h5>
                        <p className="mb-0 text-dark">Establish a Youth Respiratory Care Ambassadors Program to engage the next generation.</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-start p-3 bg-light rounded">
                      <CheckCircle style={{ color: arcaGreen }} size={24} className="me-3 mt-1 flex-shrink-0" />
                      <div>
                        <h5 className="mb-2" style={{ color: arcaGreen }}>Continued Global Collaborations</h5>
                        <p className="mb-0 text-dark">Continue and expand collaborations with ICRC, GHA, GACO, and BWI.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div 
                className="card-header text-white py-3"
                style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
              >
                <h2 className="h4 mb-0">
                  <Award size={24} className="me-2" />
                  Acknowledgments & Conclusion
                </h2>
              </div>
              <div className="card-body p-4">
                <h5 className="mb-3" style={{ color: arcaGreen }}>Acknowledgments</h5>
                <p className="text-dark mb-3">
                  ARCA thanks all members, Country Directors, volunteers, and international partners for their commitment and dedication.
                </p>
                <p className="text-dark mb-4">
                  <strong>Special appreciation to:</strong> Global Health Alliance (GHA), The Breathe Well Initiative (BWI), Global Asthma and COPD Outreach (GACO), International Council for Respiratory Care (ICRC), and Zopec Medical for their sponsorship and support.
                </p>

                <h5 className="mb-3" style={{ color: arcaGreen }}>Conclusion</h5>
                <p className="text-dark mb-0">
                  2025 was a transformative year for ARCA — marked by growth, collaboration, and expansion of respiratory care education across Africa. ARCA remains steadfast in its mission to enhance respiratory health through education, advocacy, and global cooperation.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Call to Action */}
      <div 
        className="py-5 text-white text-center"
        style={{ background: `linear-gradient(135deg, ${arcaGreen} 0%, #556B1F 100%)` }}
      >
        <div className="container">
          <h2 className="mb-4">Join Us in Our Mission</h2>
          <p className="lead mb-4">
            Be part of ARCA's journey to advance respiratory care across Africa
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <a 
              href="/membership_registration" 
              className="btn btn-light btn-lg rounded-pill px-5"
              style={{ color: arcaGreen, fontWeight: '600' }}
            >
              Become a Member
            </a>
            <a 
              href="/get-involved/donate" 
              className="btn btn-outline-light btn-lg rounded-pill px-5"
            >
              Support ARCA
            </a>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default AnnualReportsPage;
